import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Lock, X, ArrowRight, Calculator } from "lucide-react";
import { useLang } from "@/context/LangContext";

const VISITS_KEY = "tc-guest-calc-visits";
const DISMISS_KEY = "tc-gate-dismissed-at"; // sessionStorage — re-shows next session
const THRESHOLD = 3;
const MIN_HOURS_BETWEEN = 6; // don't badger on rapid revisits

/**
 * Tracks unique calculator visits while in guest mode.
 * Shows a polite signup modal after the THRESHOLD-th distinct calc visit.
 * Dismissable per session.
 */
export default function SignupGate({ guestMode }) {
  const location = useLocation();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [visitCount, setVisitCount] = useState(0);

  useEffect(() => {
    if (!guestMode) return;
    // Only count calculator pages: /tools/<something>
    const m = location.pathname.match(/^\/tools\/([a-z-]+)$/i);
    if (!m) return;
    const calcKey = m[1];

    let visits = [];
    try {
      visits = JSON.parse(localStorage.getItem(VISITS_KEY) || "[]");
    } catch {
      visits = [];
    }
    if (!visits.includes(calcKey)) {
      visits.push(calcKey);
      localStorage.setItem(VISITS_KEY, JSON.stringify(visits));
    }
    setVisitCount(visits.length);

    // Should we show the gate?
    if (visits.length < THRESHOLD) return;

    const dismissedAt = Number(sessionStorage.getItem(DISMISS_KEY) || 0);
    const hoursSince = (Date.now() - dismissedAt) / (1000 * 60 * 60);
    if (dismissedAt && hoursSince < MIN_HOURS_BETWEEN) return;

    // small delay so the user lands on the page before the modal pops
    const t = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(t);
  }, [location.pathname, guestMode]);

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="signup-gate-modal"
    >
      <div
        className="absolute inset-0 bg-black/75 backdrop-blur-sm"
        onClick={dismiss}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-lg bg-[var(--bg)] border border-[var(--border)] shadow-2xl">
        {/* hi-vis stripe */}
        <div className="hi-vis-stripe h-1.5 w-full" />

        <button
          type="button"
          onClick={dismiss}
          className="absolute top-3 right-3 text-zinc-400 hover:text-white p-1"
          aria-label="Close"
          data-testid="signup-gate-close"
        >
          <X size={18} />
        </button>

        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[var(--primary)]/15 border border-[var(--primary)]/40 flex items-center justify-center">
              <Lock size={18} className="text-[var(--primary)]" />
            </div>
            <div>
              <div className="label-mono">{visitCount} {t("gate.eyebrow", "calcs in · nice work")}</div>
              <div className="font-display text-2xl tracking-tight mt-0.5">
                {t("gate.title", "Want to save your work?")}
              </div>
            </div>
          </div>

          <p className="text-sm text-zinc-300 leading-relaxed">
            {t("gate.body", "A free account keeps your quotes, mileage logs, and budgets so you can come back to them next month — no more re-typing.")}
          </p>

          <ul className="mt-4 space-y-2 text-sm text-zinc-300">
            <li className="flex items-start gap-2">
              <Calculator size={14} className="text-[var(--primary)] mt-0.5 shrink-0" />
              <span>All 7 calculators stay free — forever.</span>
            </li>
            <li className="flex items-start gap-2">
              <Calculator size={14} className="text-[var(--primary)] mt-0.5 shrink-0" />
              <span>Save clients, quotes &amp; invoices. Export when needed.</span>
            </li>
            <li className="flex items-start gap-2">
              <Calculator size={14} className="text-[var(--primary)] mt-0.5 shrink-0" />
              <span>Email used only for your account — no spam.</span>
            </li>
          </ul>

          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <Link
              to="/register"
              onClick={dismiss}
              className="btn-primary justify-center flex-1"
              data-testid="signup-gate-register"
            >
              {t("landing.cta_register", "Create free account")} <ArrowRight size={14} />
            </Link>
            <Link
              to="/login"
              onClick={dismiss}
              className="btn-ghost justify-center flex-1"
              data-testid="signup-gate-login"
            >
              {t("landing.cta_login", "Login")}
            </Link>
          </div>

          <button
            type="button"
            onClick={dismiss}
            className="mt-3 w-full text-center text-[11px] font-mono uppercase tracking-wider text-zinc-500 hover:text-zinc-300 py-2"
            data-testid="signup-gate-continue-guest"
          >
            {t("gate.continue_guest", "Maybe later · keep using as guest")}
          </button>
        </div>
      </div>
    </div>
  );
}
